import React, { useContext, useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  Navbar,
  EmailHandler,
  Modal,
  EmailNotVerifiedNotification,
  AccountDeleted
} from 'components';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import { useWindowSize } from 'hooks';
import { LocalContext } from 'context';
import { redirect } from 'utils';
import { FirebaseContext, useAuth } from '../components/Firebase';
import '../assets/fonts/fonts.css'

const GlobalStyles = createGlobalStyle`
  * {
    margin:0;
    padding:0;
    box-sizing:border-box;
    font-family: 'Helvetica Neue';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  html{
    font-size: ${({ font }) => font};
  }

  body{
    background: ${({ theme }) => (theme.className === 'contrast' ? 'black' : 'white')};
    overflow-x:hidden;
  }

  a {
      text-decoration: none;
  }

  button {
    border:none;
    outline:none;
    &:focus{
        outline:none;
        border:none;
    }
  }

  .scroll-lock {
      overflow: hidden;
    }

  /* Address autocomplete dropdown style overrides */
  .pac-container {
    background: #093248;
    border: 0.063rem solid #fff;
    border-top-width: 0;
    border-radius: 0;
    box-shadow: none;

    @media screen and (min-width: 1440px) {
      max-width: 412px !important;
      width: 100% !important;
    }
  }

  .pac-target-input {
    outline: none;
    width: 100%;
  }

  .pac-target-input:focus {
    border: 0.188rem solid #fff !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .pac-item {
    color: #fff;
    background-color: transparent;
    font-family: 'Helvetica Neue';
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: 0.031rem;
    padding: 0.188rem 1.25rem 0;
    border: none;
    height: 2.3rem;
  }

  .pac-item:hover {
    background-color: #EB5A30;
    cursor: pointer;
  }

  .pac-logo {
    box-sizing: content-box;
    border: 0.188rem solid #fff;
    border-top: none;

    @media screen and (min-width: 1440px) {
      box-sizing: border-box;
    }
  }

  .pac-logo:after {
    padding: 0;
    height: 0;
  }

  .hdpi.pac-logo:after {
    padding: 0;
    height: 0;
  }

  .pac-icon,
  .hdpi .pac-icon {
    display: none;
  }

  .pac-item-query {
    color: #fff;
    font-size: 1rem;
  }

  .pac-item-query:after {
    content: ','
  }

  .pac-item-selected {
    background-color: #808080 !important;
  }
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-column-gap: 0.625rem;
  background: ${({ theme }) => (theme.className === 'contrast' ? 'black' : 'white')};
  filter: ${({ theme }) => (theme.className === 'grayscale' ? 'grayscale(100%)' : 'none')};
  @media (min-width: 1150px) {
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 1.25rem;
  }
`;

const Layout = ({ children }) => {
  const {
    theme,
    fontSizing,
    showUserAccountDeletedModal,
    setShowUserAccountDeletedModal,
    aNewUserHasRegisteredForAnEvent
  } = useContext(LocalContext);
  const { user, firebase, loading } = useAuth();
  const { windowWidth } = useWindowSize();
  const location = useLocation();
  const [
    returningUserHasNotConfirmedTheirEmailAddressYet,
    setReturningUserHasNotConfirmedTheirEmailAddressYet
  ] = useState(false);
  const [newUserLocalStorageData, setNewUserLocalStorageData] = useState(null);
  const queryParams = queryString.parse(location.search);
  const { mode } = queryParams;

  useEffect(() => {
    if (!loading && firebase) {
      const data = JSON.parse(window.localStorage.getItem('newUserRetina'));
      if (!firebase?.auth?.currentUser && data && !mode) {
        setNewUserLocalStorageData(data);
      }
    }
    return () => {
      // This is just for safety, though hopefully not necessary.
      if (firebase?.auth?.currentUser) {
        window.localStorage.removeItem('newUserRetina');
        window.localStorage.removeItem('existingUserRetina');
        window.localStorage.removeItem('referringUrlRetina');
      }
    };
  }, [loading, firebase]);

  useEffect(() => {
    if (newUserLocalStorageData) {
      setReturningUserHasNotConfirmedTheirEmailAddressYet(true);
    }
  }, [newUserLocalStorageData]);

  return (
    <FirebaseContext.Provider value={{ user, firebase, loading }}>
      <Wrapper theme={theme}>
        <GlobalStyles theme={theme} font={fontSizing} />
        <EmailHandler />
        <Navbar />
        {children}
        {!mode &&
          (aNewUserHasRegisteredForAnEvent || returningUserHasNotConfirmedTheirEmailAddressYet) && (
            <EmailNotVerifiedNotification
              theme={theme}
              newUserLocalStorageData={newUserLocalStorageData}
              returningUserHasNotConfirmedTheirEmailAddressYet={
                returningUserHasNotConfirmedTheirEmailAddressYet
              }
            />
          )}
        <Modal
          hideCloseButton
          openWithoutTrigger={showUserAccountDeletedModal}
          modalContent={
            <AccountDeleted
              setShowUserAccountDeletedModal={setShowUserAccountDeletedModal}
              onClickOutside={() => setShowUserAccountDeletedModal(false)}
            />
          }
        />
      </Wrapper>
    </FirebaseContext.Provider>
  );
};

export default Layout;
